import type { SupabaseClient } from "@supabase/supabase-js";

// Thin supabase-js wrappers over the console update-control backend (migration
// 20260609143000_console_backend_surface.sql):
//   - public.app_release_policy  (channel-keyed; authenticated read)
//   - public.upsert_client_app_status(...)  (SECURITY DEFINER telemetry heartbeat)
// All comparison/decision logic lives in ./version.ts; this file is just I/O + row mapping.

export const WEB_CHANNEL = "web";
export const WEB_PLATFORM = "web";

export interface ReleasePolicy {
  channel: string;
  latestVersion: string;
  minSupportedVersion: string;
  forceUpdate: boolean;
  message: string | null;
}

/** Defensively map an app_release_policy row to a typed ReleasePolicy. */
export function toReleasePolicy(row: Record<string, unknown>): ReleasePolicy {
  return {
    channel: typeof row.channel === "string" ? row.channel : WEB_CHANNEL,
    latestVersion: typeof row.latest_version === "string" ? row.latest_version : "",
    minSupportedVersion:
      typeof row.min_supported_version === "string" ? row.min_supported_version : "",
    forceUpdate: row.force_update === true,
    message: typeof row.message === "string" ? row.message : null,
  };
}

/** Read the 'web' channel release policy. Returns null on error / no row (caller stays OK). */
export async function readWebReleasePolicy(client: SupabaseClient): Promise<ReleasePolicy | null> {
  const { data, error } = await client
    .from("app_release_policy")
    .select("channel, latest_version, min_supported_version, force_update, message")
    .eq("channel", WEB_CHANNEL)
    .maybeSingle();
  if (error !== null || data === null || typeof data !== "object") {
    return null;
  }
  return toReleasePolicy(data as Record<string, unknown>);
}

export interface ClientStatusReport {
  accountId: string;
  actorId: string;
  appVersion: string;
  buildSha: string | null;
  userAgent: string | null;
}

/**
 * Report this client's version via upsert_client_app_status (the telemetry heartbeat). Best-effort:
 * any failure is swallowed — version telemetry must never surface an error into the operator UI.
 */
export async function reportClientStatus(
  client: SupabaseClient,
  report: ClientStatusReport,
): Promise<void> {
  try {
    await client.rpc("upsert_client_app_status", {
      p_account_id: report.accountId,
      p_actor_id: report.actorId,
      p_platform: WEB_PLATFORM,
      p_app_version: report.appVersion,
      p_channel: WEB_CHANNEL,
      p_build_sha: report.buildSha,
      p_user_agent: report.userAgent,
    });
  } catch {
    // Best-effort telemetry; never surfaced.
  }
}
